import type { FulfillmentOrderLine } from "../types";
import type { EntryExistingDraft, EntryParseBaseInput, EntryParseResult, EntryPreviewItem } from "./confirmationEntryTypes";
import { prepareExistingMealEntry } from "./confirmationExistingMealEntry";
import { createExistingEntrySourceMatcher, diffExistingEntry } from "./confirmationExistingEntryShared";
import { parseMealEntry } from "./confirmationMealEntryService";
import { normalized } from "./confirmationEntryPrimitives";
import { confirmationTravelerRoleLabel } from "./fulfillmentOrderEditorService";

const compact = (text: string) => normalized(text).replace(/\s+/g, "");
const identity = (line: Partial<FulfillmentOrderLine>) => JSON.stringify([
  line.dayNumbers, ({ breakfast: "早餐", lunch: "中餐", dinner: "晚餐", 午餐: "中餐" } as Record<string, string>)[line.mealType || line.itemNameSnapshot || ""] || line.mealType || line.itemNameSnapshot,
  line.unit, confirmationTravelerRoleLabel(line.travelerRole),
]);
const sameFormula = (a: Partial<FulfillmentOrderLine>, b: Partial<FulfillmentOrderLine>) =>
  identity(a) === identity(b) && ["quantity", "billingCount", "unitPriceAmount"].every(field => Number(a[field as keyof FulfillmentOrderLine]) === Number(b[field as keyof FulfillmentOrderLine]));

/** 餐厅全文只从输入生成预览；原行按日期、餐别、单位及角色匹配，歧义拒绝写入。 */
export function parseMealGroupDraft(input: EntryParseBaseInput, originals: FulfillmentOrderLine[]): EntryParseResult & {
  drafts: EntryExistingDraft[];
  locations: Record<string, { sourceStart: number; sourceEnd: number; raw: string }>;
} {
  const seeds = originals.map(base => prepareExistingMealEntry(base, input.routeDays));
  const canMatchSource = createExistingEntrySourceMatcher(seeds, input.text, identity, compact);
  const result: ReturnType<typeof parseMealGroupDraft> = { items: [], errors: [], drafts: originals.map(base => ({ base, patch: {}, removed: false })), locations: {} };
  const used = new Set<number>();
  const candidates: EntryPreviewItem[] = [];
  const aggregateCandidates = new Set<string>();
  const sources = [...input.text.matchAll(/[^\n;；]+/g)].filter(source => source[0].trim());
  const duplicateTexts = [...new Set(seeds.map(seed => compact(seed.input.text)))];
  for (const text of duplicateTexts) {
    const originalCount = seeds.filter(seed => compact(seed.input.text) === text).length;
    const currentCount = sources.filter(source => compact(source[0]) === text).length;
    if (originalCount > 1 && currentCount > 0 && currentCount < originalCount)
      result.errors.push({ raw: input.text, sourceStart: 0, sourceEnd: input.text.length, field: "formula", message: "相同日期、餐别和公式有多条原费用，无法唯一确认修改或删除对象；请从主表单独打开目标原行" });
  }
  // 完全未改的历史文本直接保留快照，避免仅打开就重算金额或强迫修复历史缺项。
  for (const source of sources) {
    const location = { raw: source[0], sourceStart: source.index, sourceEnd: source.index + source[0].length };
    const exact = seeds.findIndex((seed, index) => !used.has(index) && compact(seed.input.text) === compact(source[0])
      && canMatchSource(index, location));
    if (exact >= 0) { used.add(exact); result.locations[originals[exact].id] = location; continue; }
    const context = { ...input, text: source[0], allowZeroQuantity: originals.length > 0 };
    const aggregate = seeds.some(seed => (seed.base.dayNumbers?.length ?? 0) > 1)
      ? parseMealEntry(context, { preserveRange: true }) : null;
    const preservesOriginalRange = aggregate && !aggregate.errors.length && aggregate.items.length === 1
      && seeds.some((seed, index) => !used.has(index) && (seed.base.dayNumbers?.length ?? 0) > 1
        && identity(aggregate.items[0].patch) === identity(seed.baseline || seed.base));
    const parsed = preservesOriginalRange ? aggregate : parseMealEntry(context);
    candidates.push(...parsed.items.map((item, index) => ({ ...item, ...location, key: `${source.index}-${index}` })));
    if (preservesOriginalRange) parsed.items.forEach((_, index) => aggregateCandidates.add(source.index + "-" + index));
    result.errors.push(...parsed.errors.map(issue => ({ ...issue, ...location })));
  }
  const matched = new Map<number, number>();
  for (const exact of [true, false]) {
    candidates.forEach((item, itemIndex) => {
      if (matched.has(itemIndex)) return;
      const matches = seeds.map((seed, index) => ({ line: seed.baseline || seed.base, index }))
        .filter(({ line, index }) => !used.has(index) && (exact
          ? sameFormula(item.patch, line) && canMatchSource(index, item)
          : identity(item.patch) === identity(line)));
      if (matches.length === 1) { matched.set(itemIndex, matches[0].index); used.add(matches[0].index); }
    });
  }
  const remainingSeeds = seeds.map((_, index) => index).filter(index => !used.has(index));
  const remainingItems = candidates.map((_, index) => index).filter(index => !matched.has(index));
  if (remainingSeeds.length === 1 && remainingItems.length === 1) {
    matched.set(remainingItems[0], remainingSeeds[0]); used.add(remainingSeeds[0]);
  } else if (remainingSeeds.length && remainingItems.length) {
    result.errors.push({ raw: input.text, sourceStart: 0, sourceEnd: input.text.length, field: "formula", message: "多笔餐费无法唯一对应原行，请逐条修改或从主表打开目标原行，避免互换主体和历史金额" });
    return result;
  }
  candidates.forEach((item, index) => {
    const seedIndex = matched.get(index);
    if (seedIndex === undefined) {
      if (aggregateCandidates.has(item.key)) {
        result.errors.push({ ...item, field: "formula", message: "汇总餐费必须对应已有原行；新增餐费请按逐日规则录入" });
        return;
      }
      if (Number(item.patch.quantity) <= 0) result.errors.push({ ...item, field: "formula", message: "新增餐费数量必须大于0" });
      else result.items.push(item);
      return;
    }
    const seed = seeds[seedIndex];
    // 主体只由显式选择框修改；混合历史结算不能被组默认值覆盖。
    const parsed = { ...item.patch, fulfillmentSupplierId: seed.baseline?.fulfillmentSupplierId, fulfillmentSupplierName: seed.baseline?.fulfillmentSupplierName, settlementSupplierId: seed.baseline?.settlementSupplierId, settlementSupplierName: seed.baseline?.settlementSupplierName };
    const change = diffExistingEntry(seed, parsed, seed.baseline, item.raw, {});
    result.drafts[seedIndex].patch = change.patch;
    result.locations[seed.base.id] = item;
    result.errors.push(...change.errors.map(issue => ({ ...issue, sourceStart: item.sourceStart, sourceEnd: item.sourceEnd })));
  });
  if (!result.errors.length) result.drafts.forEach((draft, index) => { draft.removed = !used.has(index); });
  return result;
}
